// Which pile a dragged run lands on: among the slots and top cards whose box overlaps the dragged
// card's box, the LEGAL target with the largest overlap. Any overlap counts, as in the legacy drag
// (lib/drag.js: boxes that touch at all): older players drop roughly, and the 15 % minimum this used to
// ask for sent their cards back (player reports 2026-10-23, owner: keep the old margin). Replaces the
// legacy "first overlapped element in DOM order" rule (GAP-005, Q13, SPIDER-Q06/Q12).
import type { PileRef } from '../engine/types';

export interface Candidate {
  pile: PileRef;
  rect: DOMRect;
}

/** Share of the dragged card's area a target must overlap: 0 = any overlap (the legacy margin). */
export const MIN_OVERLAP = 0;

export function parsePile(pile: string | undefined): PileRef | null {
  if (!pile) return null;
  if (pile === 'stock') return { kind: 'stock' };
  if (pile === 'waste') return { kind: 'waste' };
  const m = /^(foundation|tableau|cell)-(\d+)$/.exec(pile);
  if (!m) return null;
  return { kind: m[1] as 'foundation' | 'tableau' | 'cell', index: Number(m[2]) };
}

export const pileKey = (p: PileRef): string => (p.kind === 'stock' || p.kind === 'waste' ? p.kind : `${p.kind}-${p.index}`);

/**
 * Where a card lands: its box now, moved by what is left of its glide (the transform transition of `data-moving`,
 * board/glide.ts). A card measured mid-flight made a quick next move miss it (player report 2026-10-25, "you have to
 * slowly put the card in place"; 300 ms per move in the classic mode). Its box now when it does not glide, or where
 * the browser cannot tell (no getAnimations before Chrome 84).
 */
export function landingRect(el: HTMLElement): DOMRect {
  const r = el.getBoundingClientRect();
  if (!el.hasAttribute('data-moving') || typeof el.getAnimations !== 'function') return r;
  try {
    const glide = el.getAnimations().find((a) => (a as CSSTransition).transitionProperty === 'transform');
    const frames = (glide?.effect as KeyframeEffect | null | undefined)?.getKeyframes();
    const to = frames?.[frames.length - 1]?.transform;
    if (typeof to !== 'string') return r;
    const nowCss = getComputedStyle(el).transform;
    const now = new DOMMatrixReadOnly(nowCss === 'none' ? undefined : nowCss);
    const end = new DOMMatrixReadOnly(to === 'none' ? undefined : to);
    return new DOMRect(r.left + end.e - now.e, r.top + end.f - now.f, r.width, r.height);
  } catch {
    return r;
  }
}

function overlap(a: DOMRect, b: DOMRect): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

/** Slots and the top card of every pile that is not part of the dragged run. `openPiles` (Pyramid): the waste's and
 *  the stock's top cards are drop targets too (a pair). */
export function collectCandidates(board: HTMLElement, draggedPile: string, openPiles = false): Candidate[] {
  const byPile = new Map<string, Candidate>();
  for (const el of board.querySelectorAll<HTMLElement>('.blank[data-pile]')) {
    const pile = parsePile(el.dataset.pile);
    if (pile && pile.kind !== 'stock') byPile.set(el.dataset.pile!, { pile, rect: el.getBoundingClientRect() });
  }
  // The top (highest index) visible card of each pile replaces its slot as the drop box.
  const tops = new Map<string, { index: number; el: HTMLElement }>();
  for (const el of board.querySelectorAll<HTMLElement>('.card[data-pile]:not([hidden]):not([data-dragging])')) {
    const pile = el.dataset.pile!;
    if (pile === draggedPile || pile === 'deck' || (!openPiles && (pile === 'stock' || pile === 'waste'))) continue;
    const index = Number(el.dataset.index);
    const cur = tops.get(pile);
    if (!cur || index > cur.index) tops.set(pile, { index, el });
  }
  for (const [pile, { el }] of tops) {
    const ref = parsePile(pile);
    if (ref) byPile.set(pile, { pile: ref, rect: landingRect(el) });
  }
  return [...byPile.values()];
}

export function pickDropTarget(dragged: DOMRect, candidates: Candidate[], allowed: (p: PileRef) => boolean): PileRef | null {
  const min = dragged.width * dragged.height * MIN_OVERLAP;
  let best: { pile: PileRef; area: number } | null = null;
  for (const c of candidates) {
    const area = overlap(dragged, c.rect);
    if (area <= 0 || area < min || !allowed(c.pile)) continue;
    if (!best || area > best.area) best = { pile: c.pile, area };
  }
  return best?.pile ?? null;
}
